// src/pages/providers/2_dashboard/components/PatientArchiveDialog.js
import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { XIcon } from '@heroicons/react/solid';
import { doc, serverTimestamp, collection, writeBatch } from 'firebase/firestore';
import { db, auth } from '../../../../firebase';
import { toast } from 'react-toastify';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import { emitPatientArchived } from '../../../../analytics/patientArchiveEvents';

function PatientArchiveDialog({ isOpen, onClose, patient }) {
  const { optimisticArchivePatient, refreshPatientsData } = useContext(GlobalStateContext);
  const [selectedReason, setSelectedReason] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstRadioRef = useRef(null); 

  const archiveReasons = [
    {
      id: 'completed',
      title: 'Mark Patient as Completed',
      description: 'Patient has successfully completed their assessment process',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'not_proceeding',
      title: 'Mark Patient as Not Proceeding with Assessment',
      description: 'Patient has decided not to continue with the assessment process',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    }
  ];

  const handleSubmit = async () => {
    if (!patient) return;
    if (!selectedReason) {
      toast.error('Please select a reason for archiving the patient');
      return;
    }

    if (!patient.patientId) {
      toast.error('Patient information is missing');
      return;
    }

    setIsSubmitting(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const patientDocRef = doc(db, 'patients', patient.patientId);
      const eventsColRef = collection(db, 'patients', patient.patientId, 'archive_events');

      const archiveData = {
        isArchived: true,
        archiveReason: selectedReason,
        archiveNote: note.trim() || null,
        archivedAt: serverTimestamp(),
        archivedBy: user.uid,
        archivedByName: user.displayName || 'Unknown User'
      };

      // Atomic write: update patient doc and create event
      const batch = writeBatch(db);
      batch.update(patientDocRef, archiveData);
      const eventDocRef = doc(eventsColRef);
      batch.set(eventDocRef, {
        action: 'archive',
        reason: selectedReason,
        note: note.trim() || null,
        byUserId: user.uid,
        byUserName: user.displayName || 'Unknown User',
        at: serverTimestamp()
      });
      await batch.commit();

      // Analytics mirror of the state change that just persisted. The selected
      // reason travels; the typed note is reduced to whether there was one.
      emitPatientArchived({
        patientId: patient.patientId,
        reason: selectedReason,
        note: note.trim() || null,
      });

      // Optimistic (post-success) UI update:
      // Update the table immediately without waiting for the refetch.
      try {
        optimisticArchivePatient(patient.patientId, {
          archiveReason: selectedReason,
          archiveNote: note.trim() || null,
          archivedAt: Date.now(),
          archivedBy: user.uid,
          archivedByName: user.displayName || 'Unknown User',
        });
      } catch (_) {}

      toast.success(`Patient ${patient.firstName} ${patient.lastName} has been archived`);
      // No force-refresh here — the optimistic state IS the truth (the write succeeded).
      // The next natural SWR cycle (30s TTL) will confirm with the server.
      // Force-refreshing immediately would overwrite the optimistic state with stale
      // data from patient_summaries (trigger may not have updated the summary yet).

      // Reset form
      setSelectedReason('');
      setNote('');
      
      onClose();

    } catch (error) {
      console.error('Error archiving patient:', error);
      toast.error('Failed to archive patient. Please try again.');
      // Ensure list is consistent even if we failed mid-flight
      try { await refreshPatientsData({ force: true }); } catch (_) {}
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setSelectedReason('');
      setNote('');
      onClose();
    }
  }, [isSubmitting, onClose]);

  // Initial focus on first option when opened (Radix manages focus trapping/restore)
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (firstRadioRef.current) {
          try { firstRadioRef.current.focus(); } catch (e) {}
        }
      }, 0);
    }
  }, [isOpen]);

  return (
    <AlertDialog.Root open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg p-6 max-w-md w-[90vw] max-h-[85vh] overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <AlertDialog.Title className="text-lg font-semibold leading-6 text-gray-900">
              Archive Patient
            </AlertDialog.Title>
            <AlertDialog.Description className="sr-only">
              Choose a reason to archive this patient and optionally include a note. This action can be reversed by restoring the patient later.
            </AlertDialog.Description>
            <AlertDialog.Cancel asChild>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <XIcon className="h-5 w-5 text-gray-400" />
              </button>
            </AlertDialog.Cancel>
          </div>

          {patient && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Patient:</span> {patient.firstName} {patient.lastName}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Email:</span> {patient.email}
              </p>
              {patient.customFields?.referenceNumber && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Reference:</span> {patient.customFields.referenceNumber}
                </p>
              )}
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Please select a reason for archiving this patient:
            </label>
            <div className="space-y-3">
              {archiveReasons.map((reason) => {
                return (
                  <div
                    key={reason.id}
                    className={`relative rounded-lg border-2 p-4 cursor-pointer transition-all duration-200 ${
                      selectedReason === reason.id
                        ? `${reason.borderColor} ${reason.bgColor}`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedReason(reason.id)}
                  >
                    <div className="flex items-start">
                      <div className="ml-3 flex-grow">
                    <div className="flex items-top">
                          <input
                            type="radio"
                            name="archiveReason"
                            value={reason.id}
                            checked={selectedReason === reason.id}
                        onChange={() => setSelectedReason(reason.id)}
                        ref={reason.id === 'completed' ? firstRadioRef : null}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <label className="ml-2 text-sm font-medium text-gray-900">
                            {reason.title}
                          </label>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 ml-6">
                          {reason.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add any additional context or notes..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-violet-500 focus:border-violet-500 text-sm"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <AlertDialog.Cancel asChild>
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </AlertDialog.Cancel>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedReason}
              className="px-4 py-2 text-sm font-semibold text-white bg-violet-600 border border-transparent rounded-md hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Archiving...
                </div>
              ) : (
                'Archive Patient'
              )}
            </button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export default PatientArchiveDialog;